// Settings → « Fournisseurs IA » (owner, 2026-10-23).
//
//   Fournisseurs        OpenRouter + Anthropic (Claude): key (never shown, only
//                       ••••last4 and where it comes from), ON/OFF, « Tester »,
//                       monthly budget and this month's spend; Anthropic also has
//                       the Admin key, the free credit, the console amount, the
//                       daily check and the editable price table.
//   Tâches              which provider + model runs each AI task, and its fallback.
//   Réponses WhatsApp   the easy / complex router (Haiku / Sonnet) and its rules.
//   Estimation          what each task would cost on each Claude model.
//
// Data: worker routes/aiProviders.ts via lib/aiProvidersApi.ts (admin only on
// the server). Night mode: tokens only (dashboard.night.guard.test.ts scans this
// file); phones: no table — every row is a stacked card under md.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import { aiFeatureLabel } from '@/lib/aiFeatures';
import {
  aiProvidersApi,
  type AiProvidersState, type KeySlot, type ModelPriceRow, type PriceRow, type ProviderId,
  type PublicKeyInfo, type ReplyRouterCfg, type ReplyRules, type TaskRow, type TestResult,
} from '@/lib/aiProvidersApi';
import {
  budgetBar, clampRules, fmtMad, fmtUsd, formToRoute, frDay, isDefaultForm, joinWords, parseAmount,
  routeOptions, routeToForm, sameForm, splitWords, withPrimary, RULE_LISTS,
  type RouteForm, type RuleListKey, type Tone,
} from '@/lib/aiProvidersUi';

export const AI_STATE_KEY = ['ai-providers-state'] as const;

export function useAiProvidersState() {
  return useQuery({ queryKey: AI_STATE_KEY, queryFn: aiProvidersApi.state, staleTime: 30_000 });
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: AI_STATE_KEY });
}

// ─── shared bits ─────────────────────────────────────────────────────────────

export const inputCls = 'w-full min-w-0 rounded-lg border border-adm-line-strong bg-adm-surface px-2.5 py-2 text-base md:text-sm text-adm-ink';
const labelCls = 'block text-[11.5px] font-medium text-adm-muted mb-1';

function Card({ title, description, right, children }: { title: string; description?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-adm-surface rounded-xl border border-adm-line p-4 sm:p-5 min-w-0">
      <div className="mb-4 flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-adm-ink">{title}</h2>
          {description && <p className="text-xs text-adm-muted mt-0.5">{description}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4 pt-4 border-t border-adm-line">
      <h3 className="text-[13px] font-semibold text-adm-ink mb-2">{title}</h3>
      {children}
    </div>
  );
}

function Loading() {
  const { t } = useT();
  return <p className="text-sm text-adm-muted">{t('Chargement…')}</p>;
}

function LoadError() {
  const { t } = useT();
  return <p className="text-sm text-status-stuck">{t('Réglages IA indisponibles. Réessayez.')}</p>;
}

export function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      onClick={() => onChange(!on)}
      className="inline-flex items-center gap-2 text-xs font-semibold disabled:opacity-50 min-h-11 md:min-h-0"
    >
      <span className={`relative inline-block w-10 h-6 rounded-full transition-colors ${on ? 'bg-status-done-solid' : 'bg-adm-line-strong'}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-adm-surface shadow transition-all ${on ? 'left-[18px]' : 'left-0.5'}`} />
      </span>
      <span className={on ? 'text-status-done' : 'text-adm-muted'}>{on ? 'ON' : 'OFF'}</span>
    </button>
  );
}

const TONE_FILL: Record<Tone, string> = { ok: 'bg-adm-gold', amber: 'bg-status-waiting-solid', red: 'bg-status-stuck-solid' };
const TONE_TEXT: Record<Tone, string> = { ok: 'text-adm-ink-2', amber: 'text-status-waiting', red: 'text-status-stuck' };

export function Bar({ pct, tone }: { pct: number; tone: Tone }) {
  return (
    <span className="block h-2 rounded-full bg-adm-surface-3 overflow-hidden" role="presentation">
      <span data-tone={tone} className={`block h-full rounded-full ${TONE_FILL[tone]}`} style={{ width: `${Math.min(100, Math.max(pct > 0 ? 2 : 0, pct))}%` }} />
    </span>
  );
}

function Saved({ show }: { show: boolean }) {
  const { t } = useT();
  return show ? <span className="text-xs font-semibold text-status-done">✓ {t('Enregistré')}</span> : null;
}

function ErrorLine({ error }: { error: unknown }) {
  return error ? <p className="mt-1 text-xs text-status-stuck">{(error as Error).message || 'Erreur'}</p> : null;
}

/** `true` for 2 s after each success. */
function useFlash(): [boolean, () => void] {
  const [on, setOn] = useState(false);
  useEffect(() => { if (!on) return; const id = setTimeout(() => setOn(false), 2000); return () => clearTimeout(id); }, [on]);
  return [on, () => setOn(true)];
}

const PROVIDER_NAME: Record<ProviderId, string> = { openrouter: 'OpenRouter', anthropic: 'Anthropic (Claude)' };

// ─── key row ─────────────────────────────────────────────────────────────────

function sourceLabel(src: PublicKeyInfo['source']): string {
  return src === 'stored' ? 'enregistrée ici' : src === 'env' ? 'secret du worker' : 'aucune';
}

function TestLine({ result, error }: { result: TestResult | undefined; error: unknown }) {
  const { t } = useT();
  if (error) return <p className="text-xs text-status-stuck mt-1" data-test-result="error">{t('Échec du test')} : {(error as Error).message}</p>;
  if (!result) return null;
  return result.ok
    ? <p className="text-xs text-status-done font-semibold mt-1" data-test-result="ok">✓ OK · {result.ms} ms{result.model ? ` · ${result.model}` : ''}</p>
    : <p className="text-xs text-status-stuck mt-1" data-test-result="fail">{t('Échec du test')} : {result.error ?? '—'} · {result.ms} ms</p>;
}

export function KeyRow({ slot, info, encReady, title, hint }: { slot: KeySlot; info: PublicKeyInfo; encReady: boolean; title: string; hint?: string }) {
  const { t } = useT();
  const refresh = useRefresh();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const close = () => { setEditing(false); setValue(''); };
  const save = useMutation({ mutationFn: () => aiProvidersApi.saveKey(slot, value.trim()), onSuccess: () => { close(); refresh(); } });
  const del = useMutation({ mutationFn: () => aiProvidersApi.deleteKey(slot), onSuccess: refresh });
  const test = useMutation({ mutationFn: () => aiProvidersApi.test(slot) });

  return (
    <div data-key-slot={slot}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* min-w-48: on a phone the buttons drop under the key instead of squeezing it */}
        <div className="min-w-48 flex-1">
          <p className="text-[12.5px] font-semibold text-adm-ink">{title}</p>
          <p className="text-xs text-adm-muted">
            <span className="font-mono text-adm-ink-2">{info.last4 ? `••••${info.last4}` : '—'}</span>
            {' · '}{t(sourceLabel(info.source))}
            {info.updatedAt && info.source === 'stored' ? ` · ${t('le {d}', { d: frDay(info.updatedAt) })}` : ''}
          </p>
          {hint && <p className="text-[11.5px] text-adm-faint mt-0.5">{hint}</p>}
        </div>
        {!editing && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>{t('Changer la clé')}</Button>
            <Button variant="secondary" size="sm" loading={test.isPending} disabled={info.source === 'none'} onClick={() => test.mutate()}>{t('Tester')}</Button>
          </div>
        )}
      </div>
      {editing && (
        <form
          className="mt-2 space-y-2"
          onSubmit={(e) => { e.preventDefault(); if (encReady && value.trim()) save.mutate(); }}
        >
          {!encReady && (
            <p className="text-xs font-semibold text-status-stuck" data-enc-missing>
              {t('Le secret AI_KEYS_ENC_KEY n’est pas créé : impossible d’enregistrer une clé')}
            </p>
          )}
          <input
            type="password" autoComplete="off" spellCheck={false} value={value}
            onChange={(e) => setValue(e.target.value)} aria-label={t('Nouvelle clé')} placeholder={t('Collez la nouvelle clé')}
            className={inputCls} disabled={!encReady}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" loading={save.isPending} disabled={!encReady || !value.trim()}>{t('Enregistrer')}</Button>
            <Button type="button" variant="secondary" size="sm" onClick={close}>{t('Annuler')}</Button>
            {info.source === 'stored' && (
              <Button type="button" variant="ghost" size="sm" loading={del.isPending}
                onClick={() => { if (window.confirm(t('Retirer la clé enregistrée ici ?'))) del.mutate(); }}>
                {t('Retirer la clé enregistrée')}
              </Button>
            )}
          </div>
          <ErrorLine error={save.error ?? del.error} />
        </form>
      )}
      <TestLine result={test.data} error={test.error} />
    </div>
  );
}

// ─── provider card ───────────────────────────────────────────────────────────

function SpendLine({ spent, budget, fxUsd }: { spent: number; budget: number | null; fxUsd: number }) {
  const { t } = useT();
  const bar = budgetBar(spent, budget);
  return (
    <div className="space-y-1.5" data-spend>
      <p className="text-xs text-adm-muted">
        {t('Dépensé ce mois')} : <b className={bar ? TONE_TEXT[bar.tone] : 'text-adm-ink'}>{fmtUsd(spent, 2)}</b>
        <span> · {fmtMad(spent, fxUsd)}</span>
        {bar ? <span> · {t('sur {b} ({p} %)', { b: fmtUsd(budget, 0), p: Math.round(bar.pct) })}</span> : <span> · {t('pas de budget')}</span>}
      </p>
      {bar && <Bar pct={bar.pct} tone={bar.tone} />}
    </div>
  );
}

function BudgetField({ provider, budget }: { provider: ProviderId; budget: number | null }) {
  const { t } = useT();
  const refresh = useRefresh();
  const [v, setV] = useState(budget == null ? '' : String(budget));
  useEffect(() => setV(budget == null ? '' : String(budget)), [budget]);
  const [flash, setFlash] = useFlash();
  const save = useMutation({ mutationFn: () => aiProvidersApi.saveProvider(provider, { monthlyBudgetUsd: parseAmount(v) }), onSuccess: () => { setFlash(); refresh(); } });
  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <label className="min-w-0 flex-1 sm:flex-none sm:w-40">
        <span className={labelCls}>{t('Budget mensuel (USD)')}</span>
        <input inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} placeholder="—" className={inputCls} />
      </label>
      <Button type="submit" variant="secondary" size="sm" loading={save.isPending}>{t('Enregistrer')}</Button>
      <Saved show={flash} />
      <ErrorLine error={save.error} />
    </form>
  );
}

/**
 * Claude « espace de travail » (2026-10-23): a key made for the whole
 * organisation must name the workspace it bills — without it every call is
 * refused (HTTP 400 « not scoped to a workspace »). Empty = a key made for one
 * workspace, nothing to add.
 */
function WorkspaceField({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useT();
  const refresh = useRefresh();
  const [v, setV] = useState(workspaceId ?? '');
  useEffect(() => setV(workspaceId ?? ''), [workspaceId]);
  const [flash, setFlash] = useFlash();
  const save = useMutation({ mutationFn: () => aiProvidersApi.saveProvider('anthropic', { workspaceId: v.trim() || null }), onSuccess: () => { setFlash(); refresh(); } });
  return (
    <form className="space-y-1.5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }} data-workspace>
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-0 flex-1">
          <span className={labelCls}>{t('ID de l’espace de travail')}</span>
          <input value={v} onChange={(e) => setV(e.target.value)} placeholder="wrkspc_…" spellCheck={false} autoCapitalize="off" autoCorrect="off" className={`${inputCls} font-mono`} />
        </label>
        <Button type="submit" variant="secondary" size="sm" loading={save.isPending}>{t('Enregistrer')}</Button>
        <Saved show={flash} />
      </div>
      <p className="text-xs text-adm-muted">{t('Seulement si votre clé est liée à toute l’organisation. Console Claude › Settings › Workspaces › colonne « ID ». Ensuite, « Tester ».')}</p>
      <ErrorLine error={save.error} />
    </form>
  );
}

function ProviderCardView({ state, provider }: { state: AiProvidersState; provider: ProviderId }) {
  const { t } = useT();
  const refresh = useRefresh();
  const p = state.providers[provider];
  const toggle = useMutation({ mutationFn: (on: boolean) => aiProvidersApi.saveProvider(provider, { enabled: on }), onSuccess: refresh });
  return (
    <div data-provider={provider} className="rounded-xl border border-adm-line bg-adm-surface-2 p-3.5 sm:p-4 min-w-0 space-y-3.5">
      <div className="flex items-center gap-3">
        <h3 className="text-[14px] font-semibold text-adm-ink flex-1 min-w-0">{PROVIDER_NAME[provider]}</h3>
        <Toggle on={p.enabled} onChange={(v) => toggle.mutate(v)} label={t('Activer {p}', { p: PROVIDER_NAME[provider] })} disabled={toggle.isPending} />
      </div>
      {provider === 'openrouter' && (
        <p className="text-xs text-adm-muted rounded-lg bg-adm-gold-wash px-2.5 py-1.5">🎙 {t('Garde les messages vocaux (Claude n’entend pas l’audio)')}</p>
      )}
      <ErrorLine error={toggle.error} />
      <KeyRow slot={provider} info={p.key} encReady={state.encReady} title={t('Clé API')} />
      {provider === 'anthropic' && <WorkspaceField workspaceId={p.workspaceId ?? null} />}
      <SpendLine spent={p.monthSpentUsd} budget={p.monthlyBudgetUsd} fxUsd={state.fxUsd} />
      <BudgetField provider={provider} budget={p.monthlyBudgetUsd} />
    </div>
  );
}

// ─── Anthropic extras ────────────────────────────────────────────────────────

function CreditBlock({ state }: { state: AiProvidersState }) {
  const { t } = useT();
  const refresh = useRefresh();
  const c = state.credit;
  const [amount, setAmount] = useState(c.amountUsd == null ? '100' : String(c.amountUsd));
  const [start, setStart] = useState(c.start ?? '');
  const [end, setEnd] = useState(c.end ?? '');
  const [flash, setFlash] = useFlash();
  const save = useMutation({
    mutationFn: () => aiProvidersApi.saveProvider('anthropic', { creditUsd: parseAmount(amount), creditStart: start || null, creditEnd: end || null }),
    onSuccess: () => { setFlash(); refresh(); },
  });
  const tone: Tone = c.pctUsed == null ? 'ok' : c.pctUsed >= 100 ? 'red' : c.pctUsed >= 80 ? 'amber' : 'ok';
  return (
    <div data-credit>
      <form className="grid grid-cols-1 sm:grid-cols-[repeat(3,minmax(0,1fr))_auto] gap-2 items-end" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <label className="min-w-0"><span className={labelCls}>{t('Montant (USD)')}</span>
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} /></label>
        <label className="min-w-0"><span className={labelCls}>{t('Début')}</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></label>
        <label className="min-w-0"><span className={labelCls}>{t('Fin / renouvellement')}</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} /></label>
        <div className="flex items-center gap-2"><Button type="submit" variant="secondary" size="sm" loading={save.isPending}>{t('Enregistrer')}</Button><Saved show={flash} /></div>
      </form>
      <ErrorLine error={save.error} />
      {c.amountUsd != null && (
        <div className="mt-3 space-y-1.5">
          <p className="text-xs text-adm-muted">
            {t('Utilisé')} : <b className="text-adm-ink">{fmtUsd(c.usedUsd, 2)}</b>
            {' · '}{t('Reste')} : <b className="text-adm-ink">{fmtUsd(c.leftUsd, 2)}</b>
            {c.pctUsed != null && <> · <b className={TONE_TEXT[tone]}>{t('{p} % utilisé', { p: Math.round(c.pctUsed) })}</b></>}
            {c.daysLeft != null && <> · {t('{n} jours restants', { n: c.daysLeft })}</>}
          </p>
          {c.pctUsed != null && <Bar pct={c.pctUsed} tone={tone} />}
          <p className={`text-xs ${c.runsOutBeforeEnd ? 'text-status-stuck font-semibold' : 'text-adm-muted'}`} data-credit-forecast={c.runsOutBeforeEnd ? 'early' : 'ok'}>
            {c.runOutDate
              ? t('Au rythme de {r}/jour (7 derniers jours), le crédit sera épuisé le {d}', { r: fmtUsd(c.rate7dUsd, 2), d: frDay(c.runOutDate) })
              : t('Au rythme de {r}/jour (7 derniers jours), le crédit ne sera pas épuisé avant la fin', { r: fmtUsd(c.rate7dUsd, 2) })}
            {c.runsOutBeforeEnd && c.end ? ` — ${t('avant la fin ({d})', { d: frDay(c.end) })}` : ''}
          </p>
        </div>
      )}
    </div>
  );
}

function ConsoleBlock({ state }: { state: AiProvidersState }) {
  const { t } = useT();
  const refresh = useRefresh();
  const [amount, setAmount] = useState(state.consoleAmountUsd == null ? '' : String(state.consoleAmountUsd));
  const [date, setDate] = useState(state.consoleAmountDate ?? '');
  const [flash, setFlash] = useFlash();
  const save = useMutation({
    mutationFn: () => aiProvidersApi.saveProvider('anthropic', { consoleAmountUsd: parseAmount(amount), consoleAmountDate: date || null }),
    onSuccess: () => { setFlash(); refresh(); },
  });
  const chk = state.lastCheck;
  return (
    <div>
      <p className="text-xs text-adm-muted mb-2">{t('Le montant affiché dans la console Anthropic. Utilisé pour le contrôle quotidien quand il n’y a pas de clé Admin.')}</p>
      <form className="grid grid-cols-1 sm:grid-cols-[repeat(2,minmax(0,1fr))_auto] gap-2 items-end" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <label className="min-w-0"><span className={labelCls}>{t('Montant console (USD)')}</span>
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="—" className={inputCls} /></label>
        <label className="min-w-0"><span className={labelCls}>{t('Date')}</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></label>
        <div className="flex items-center gap-2"><Button type="submit" variant="secondary" size="sm" loading={save.isPending}>{t('Enregistrer')}</Button><Saved show={flash} /></div>
      </form>
      <ErrorLine error={save.error} />
      <div className="mt-3" data-last-check={chk ? (chk.flagged ? 'flagged' : 'ok') : 'none'}>
        {!chk ? (
          <p className="text-xs text-adm-muted">{t('Contrôle quotidien : pas encore de journée vérifiée.')}</p>
        ) : (
          <p className={`text-xs ${chk.flagged ? 'text-status-waiting font-semibold' : 'text-adm-muted'}`}>
            {chk.flagged ? '⚠ ' : '✓ '}
            {t('Contrôle du {d} ({s}) : nous {o} · Anthropic {a} · écart {g}', {
              d: frDay(chk.day), s: chk.source === 'admin_api' ? t('clé Admin') : t('console'),
              o: fmtUsd(chk.oursUsd, 2), a: fmtUsd(chk.anthropicUsd, 2),
              g: chk.gapPct == null ? '—' : `${Math.abs(Math.round(chk.gapPct * 10) / 10)} %`,
            })}
            <span className="font-normal text-adm-faint"> · {t('depuis le {d}', { d: frDay(chk.periodFrom) })}</span>
          </p>
        )}
      </div>
    </div>
  );
}

const PRICE_FIELDS: Array<{ k: keyof PriceRow; label: string }> = [
  { k: 'input', label: 'Entrée (tokens)' },
  { k: 'write5m', label: 'Écriture cache 5 min' },
  { k: 'write1h', label: 'Écriture cache 1 h' },
  { k: 'cacheRead', label: 'Lecture cache' },
  { k: 'output', label: 'Sortie (tokens)' },
];

type PriceText = Record<keyof PriceRow, string>;
const toText = (r: PriceRow | null): PriceText => Object.fromEntries(PRICE_FIELDS.map(({ k }) => [k, r ? String(r[k]) : ''])) as PriceText;
const fromText = (r: PriceText): PriceRow | null => {
  const out = {} as PriceRow;
  for (const { k } of PRICE_FIELDS) { const n = parseAmount(r[k]); if (n == null) return null; out[k] = n; }
  return out;
};

function PriceFields({ value, onChange, idPrefix }: { value: PriceText; onChange: (v: PriceText) => void; idPrefix: string }) {
  const { t } = useT();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {PRICE_FIELDS.map(({ k, label }) => (
        <label key={k} className="min-w-0" htmlFor={`${idPrefix}-${k}`}>
          <span className={labelCls}>{t(label)}</span>
          <input id={`${idPrefix}-${k}`} inputMode="decimal" value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} className={inputCls} />
        </label>
      ))}
    </div>
  );
}

function PriceEditor({ row, label }: { row: ModelPriceRow; label: string }) {
  const { t } = useT();
  const refresh = useRefresh();
  const [base, setBase] = useState<PriceText>(() => toText(row));
  const [long, setLong] = useState<PriceText>(() => toText(row.long));
  const [above, setAbove] = useState(row.longAbove == null ? '' : String(row.longAbove));
  const hasLong = row.longAbove != null || row.long != null;
  const [flash, setFlash] = useFlash();
  const parsed = fromText(base);
  const parsedLong = hasLong && above.trim() ? fromText(long) : null;
  const valid = !!parsed && (!hasLong || !above.trim() || !!parsedLong);
  const save = useMutation({
    mutationFn: () => aiProvidersApi.savePrice({
      model: row.model, ...parsed!,
      longAbove: hasLong && above.trim() ? Math.round(Number(above)) : null,
      long: parsedLong,
    }),
    onSuccess: () => { setFlash(); refresh(); },
  });
  return (
    <form data-price={row.model} className="rounded-lg border border-adm-line p-3 space-y-2" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[12.5px] font-semibold text-adm-ink flex-1 min-w-0">{label} <span className="font-mono font-normal text-adm-faint text-[11px]">{row.model}</span></p>
        <Saved show={flash} />
        <Button type="submit" variant="secondary" size="sm" loading={save.isPending} disabled={!valid}>{t('Enregistrer')}</Button>
      </div>
      <PriceFields value={base} onChange={setBase} idPrefix={`${row.model}-base`} />
      {hasLong && (
        <div className="pt-2 border-t border-adm-line space-y-2">
          <label className="flex flex-wrap items-center gap-2 text-xs text-adm-muted">
            {t('Prompt long : au-delà de')}
            <input inputMode="numeric" value={above} onChange={(e) => setAbove(e.target.value)} className={inputCls.replace('w-full ', 'w-32 ')} aria-label={t('Seuil en tokens')} />
            {t('tokens')}
          </label>
          <PriceFields value={long} onChange={setLong} idPrefix={`${row.model}-long`} />
        </div>
      )}
      <ErrorLine error={save.error} />
    </form>
  );
}

// ─── a. Fournisseurs ─────────────────────────────────────────────────────────

export function AiProvidersSection() {
  const { t } = useT();
  const q = useAiProvidersState();
  const s = q.data;
  return (
    <Card
      title={t('Fournisseurs IA')}
      description={t('Clés, interrupteurs et budgets. Une clé n’est jamais affichée : seulement ses 4 derniers caractères.')}
      right={<Link to="/admin/ai-compare" className="text-xs font-semibold text-adm-gold-ink hover:underline whitespace-nowrap">{t('Comparer les modèles')} →</Link>}
    >
      {q.isLoading && <Loading />}
      {q.error && <LoadError />}
      {s && (
        <div className="space-y-4">
          {!s.encReady && (
            <p className="text-xs font-semibold text-status-stuck rounded-lg bg-status-stuck-bg px-3 py-2">
              {t('Le secret AI_KEYS_ENC_KEY n’est pas créé : impossible d’enregistrer une clé')}
            </p>
          )}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <ProviderCardView state={s} provider="openrouter" />
            <ProviderCardView state={s} provider="anthropic" />
          </div>
          <div data-anthropic-extras>
            <Sub title={t('Anthropic — clé Admin')}>
              <KeyRow slot="anthropic_admin" info={s.anthropicAdminKey} encReady={s.encReady} title={t('Clé Admin (facultative)')}
                hint={t('Sert seulement au contrôle quotidien des coûts (ce que nous comptons vs ce qu’Anthropic facture).')} />
            </Sub>
            <Sub title={t('Anthropic — crédit gratuit')}><CreditBlock key={`${s.credit.amountUsd}-${s.credit.start}-${s.credit.end}`} state={s} /></Sub>
            <Sub title={t('Anthropic — montant console et contrôle quotidien')}><ConsoleBlock key={`${s.consoleAmountUsd}-${s.consoleAmountDate}`} state={s} /></Sub>
            <Sub title={t('Tarifs (USD par million de tokens)')}>
              <div className="space-y-2">
                {s.prices.map((r) => (
                  <PriceEditor key={`${r.model}-${JSON.stringify(r)}`} row={r} label={s.models.anthropic.find((m) => m.id === r.model)?.label ?? r.model} />
                ))}
              </div>
            </Sub>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─── b. Tâches ───────────────────────────────────────────────────────────────

// Every row is its own grid, so no column may be `auto` — the actions column is fixed too.
const TASK_GRID = 'md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(0,1.5fr)_76px_60px_72px_224px] md:gap-2.5 md:items-center';

function Select({ value, options, onChange, label }: { value: string; options: Array<{ value: string; label: string }>; onChange: (v: string) => void; label: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className={`${inputCls} truncate`}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Cell({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <span className={`${labelCls} md:hidden`}>{label}</span>
      {children}
    </div>
  );
}

function TaskRowEditor({ row, state }: { row: TaskRow; state: AiProvidersState }) {
  const { t } = useT();
  const refresh = useRefresh();
  const saved = useMemo(() => routeToForm(row.route), [row.route]);
  const [form, setForm] = useState<RouteForm>(saved);
  useEffect(() => setForm(saved), [saved]);
  const [flash, setFlash] = useFlash();
  const save = useMutation({
    mutationFn: () => (isDefaultForm(form) ? aiProvidersApi.resetRoute(row.feature) : aiProvidersApi.saveRoute(row.feature, formToRoute(form, row))),
    onSuccess: () => { setFlash(); refresh(); },
  });
  const reset = useMutation({ mutationFn: () => aiProvidersApi.resetRoute(row.feature), onSuccess: () => { setForm(routeToForm(null)); setFlash(); refresh(); } });
  const dirty = !sameForm(form, saved);
  const primaryOpts = routeOptions(row, state.models, 'primary', t);
  const fallbackOpts = routeOptions(row, state.models, 'fallback', t).filter((o) => o.value !== form.primary);

  return (
    <div data-task={row.feature} className={`py-3 border-t border-adm-line space-y-2 md:space-y-0 ${TASK_GRID}`}>
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-adm-ink truncate" title={row.feature}>{t(aiFeatureLabel(row.feature))}</p>
        <p className="text-[11px] text-adm-faint truncate font-mono" title={row.currentModel}>
          {row.route && <span className="mr-1 font-sans text-[10px] font-bold uppercase tracking-wide text-adm-gold-ink">{t('modifié')}</span>}
          {row.currentModel || '—'}
        </p>
        {row.audio && <p className="text-[11px] text-adm-muted">{t('Audio : OpenRouter seulement')}</p>}
      </div>
      <Cell label={t('Principal')}>
        <Select value={form.primary} options={primaryOpts} onChange={(v) => setForm((f) => withPrimary(f, v))} label={t('Principal — {task}', { task: t(aiFeatureLabel(row.feature)) })} />
      </Cell>
      <Cell label={t('Secours')}>
        <Select value={form.fallback} options={fallbackOpts} onChange={(v) => setForm((f) => ({ ...f, fallback: v }))} label={t('Secours — {task}', { task: t(aiFeatureLabel(row.feature)) })} />
      </Cell>
      <Cell label={t('Image (côté long)')} className={row.vision ? '' : 'hidden md:block'}>
        {row.vision
          ? <input inputMode="numeric" value={form.imageLongSide} placeholder="768" onChange={(e) => setForm((f) => ({ ...f, imageLongSide: e.target.value.replace(/\D/g, '') }))}
              aria-label={t('Côté long de l’image — {task}', { task: t(aiFeatureLabel(row.feature)) })} className={inputCls} />
          : <span className="text-xs text-adm-faint">—</span>}
      </Cell>
      <div className="grid grid-cols-2 gap-2 md:contents">
        <Cell label={t('Appels / 30 j')}>
          <span className="text-[12.5px] tabular-nums text-adm-ink">{row.calls30d.toLocaleString('fr-FR')}</span>
        </Cell>
        <Cell label={t('Coût / mois')}>
          <span className="block text-[12.5px] tabular-nums text-adm-ink">{fmtUsd(row.cost30dUsd, 2)}</span>
          <span className="block text-[10.5px] text-adm-faint tabular-nums">{fmtMad(row.cost30dUsd, state.fxUsd)}</span>
        </Cell>
      </div>
      {/* Phones: no action row while there is nothing to save or reset. */}
      <div className={`${dirty || row.route || flash ? 'flex' : 'hidden md:flex'} flex-wrap items-center gap-1.5 md:justify-end`}>
        <Saved show={flash} />
        <Button size="sm" variant={dirty ? 'primary' : 'secondary'} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t('Enregistrer')}</Button>
        {row.route && <Button size="sm" variant="ghost" loading={reset.isPending} onClick={() => reset.mutate()}>{t('Réinitialiser')}</Button>}
      </div>
      {(save.error || reset.error) && <div className="md:col-span-7"><ErrorLine error={save.error ?? reset.error} /></div>}
    </div>
  );
}

export function AiTasksSection() {
  const { t } = useT();
  const q = useAiProvidersState();
  const s = q.data;
  const rows = useMemo(() => [...(s?.tasks ?? [])].sort((a, b) => b.cost30dUsd - a.cost30dUsd || b.calls30d - a.calls30d), [s?.tasks]);
  return (
    <Card
      title={t('Tâches')}
      description={t('Quel fournisseur et quel modèle pour chaque tâche IA, et le secours si le premier échoue. Par défaut : OpenRouter, le modèle actuel, aucun changement enregistré.')}
    >
      {q.isLoading && <Loading />}
      {q.error && <LoadError />}
      {s && (
        <div>
          <div className={`hidden ${TASK_GRID} pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-adm-faint`}>
            <span>{t('Tâche')}</span><span>{t('Principal')}</span><span>{t('Secours')}</span><span>{t('Image')}</span>
            <span>{t('Appels / 30 j')}</span><span>{t('Coût / mois')}</span><span />
          </div>
          {rows.map((r) => <TaskRowEditor key={r.feature} row={r} state={s} />)}
        </div>
      )}
    </Card>
  );
}

// ─── c. Réponses WhatsApp : facile / complexe ────────────────────────────────

const RULE_LABEL: Record<RuleListKey, string> = {
  money_words: 'Mots « argent » (toujours complexe)',
  complaint_words: 'Mots « plainte » (toujours complexe)',
  edit_words: 'Mots « changement d’album » (toujours complexe)',
  easy_words: 'Mots « faciles » (salutations, merci…)',
  darija_markers: 'Marqueurs de darija',
};

function ReplyRouterBlock({ cfg, state }: { cfg: ReplyRouterCfg; state: AiProvidersState }) {
  const { t } = useT();
  const refresh = useRefresh();
  const [enabled, setEnabled] = useState(cfg.enabled);
  const [easy, setEasy] = useState(cfg.easyModel ?? '');
  const [complex, setComplex] = useState(cfg.complexModel ?? '');
  const [lists, setLists] = useState<Record<RuleListKey, string>>(() => Object.fromEntries(RULE_LISTS.map((k) => [k, joinWords(cfg.rules[k])])) as Record<RuleListKey, string>);
  const [nums, setNums] = useState({ max_easy_chars: String(cfg.rules.max_easy_chars), max_easy_questions: String(cfg.rules.max_easy_questions), sticky_minutes: String(cfg.rules.sticky_minutes) });
  const [flash, setFlash] = useFlash();
  const isClaude = cfg.provider === 'anthropic';
  const models: Array<{ value: string; label: string }> = isClaude
    ? state.models.anthropic.map((m) => ({ value: m.id, label: m.label }))
    : state.models.openrouter.map((m) => ({ value: m, label: m }));
  const withBlank = [{ value: '', label: '—' }, ...models];
  const rules = (): ReplyRules => clampRules({
    ...(Object.fromEntries(RULE_LISTS.map((k) => [k, splitWords(lists[k])])) as Pick<ReplyRules, RuleListKey>),
    max_easy_chars: Number(nums.max_easy_chars), max_easy_questions: Number(nums.max_easy_questions), sticky_minutes: Number(nums.sticky_minutes),
  });
  const save = useMutation({
    mutationFn: () => aiProvidersApi.saveReplyRouter(cfg.provider, { enabled, easyModel: easy || null, complexModel: complex || null, rules: rules() }),
    onSuccess: () => { setFlash(); refresh(); },
  });
  const sticky = Number(nums.sticky_minutes) || cfg.rules.sticky_minutes;

  return (
    <form data-reply-router={cfg.provider} className="space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className="flex flex-wrap items-center gap-3">
        <Toggle on={enabled} onChange={setEnabled} label={t('Routeur facile / complexe — {p}', { p: PROVIDER_NAME[cfg.provider] })} />
        {!isClaude && !cfg.enabled && <span className="text-xs text-adm-muted">{t('OFF par défaut')}</span>}
      </div>
      <p className="text-xs text-adm-muted rounded-lg bg-adm-surface-2 border border-adm-line px-3 py-2">
        {t('Haiku = facile, Sonnet = complexe ; dans le doute → Sonnet ; une conversation passée en Sonnet y reste {n} minutes.', { n: sticky })}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="min-w-0"><span className={labelCls}>{t('Modèle « facile »')}</span>
          <Select value={easy} options={withBlank} onChange={setEasy} label={t('Modèle « facile »')} /></label>
        <label className="min-w-0"><span className={labelCls}>{t('Modèle « complexe »')}</span>
          <Select value={complex} options={withBlank} onChange={setComplex} label={t('Modèle « complexe »')} /></label>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {RULE_LISTS.map((k) => (
          <label key={k} className="min-w-0">
            <span className={labelCls}>{t(RULE_LABEL[k])} <span className="text-adm-faint">· {splitWords(lists[k]).length}</span></span>
            <textarea rows={3} value={lists[k]} onChange={(e) => setLists((l) => ({ ...l, [k]: e.target.value }))} className={`${inputCls} resize-y leading-snug`} />
          </label>
        ))}
      </div>
      <p className="text-[11px] text-adm-faint">{t('Séparez les mots par des virgules.')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="min-w-0"><span className={labelCls}>{t('Facile : max. caractères')}</span>
          <input inputMode="numeric" value={nums.max_easy_chars} onChange={(e) => setNums((n) => ({ ...n, max_easy_chars: e.target.value }))} className={inputCls} /></label>
        <label className="min-w-0"><span className={labelCls}>{t('Facile : max. questions')}</span>
          <input inputMode="numeric" value={nums.max_easy_questions} onChange={(e) => setNums((n) => ({ ...n, max_easy_questions: e.target.value }))} className={inputCls} /></label>
        <label className="min-w-0"><span className={labelCls}>{t('Reste en complexe (minutes)')}</span>
          <input inputMode="numeric" value={nums.sticky_minutes} onChange={(e) => setNums((n) => ({ ...n, sticky_minutes: e.target.value }))} className={inputCls} /></label>
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" loading={save.isPending}>{t('Enregistrer')}</Button>
        <Saved show={flash} />
      </div>
      <ErrorLine error={save.error} />
    </form>
  );
}

export function AiReplyRouterSection() {
  const { t } = useT();
  const q = useAiProvidersState();
  const s = q.data;
  const [tab, setTab] = useState<ProviderId>('anthropic');
  return (
    <Card title={t('Réponses WhatsApp : facile / complexe')} description={t('Les messages simples vont au modèle « facile », tout le reste au modèle « complexe ».')}>
      {q.isLoading && <Loading />}
      {q.error && <LoadError />}
      {s && (
        <>
          <div role="tablist" className="flex gap-1.5 mb-3">
            {(['anthropic', 'openrouter'] as const).map((p) => (
              <button
                key={p} type="button" role="tab" aria-selected={tab === p} onClick={() => setTab(p)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border min-h-11 md:min-h-0 ${tab === p ? 'border-adm-gold text-adm-gold-ink bg-adm-gold-wash' : 'border-adm-line text-adm-muted'}`}
              >
                {PROVIDER_NAME[p]} · {s.replyRouter[p].enabled ? 'ON' : 'OFF'}
              </button>
            ))}
          </div>
          <ReplyRouterBlock key={`${tab}-${JSON.stringify(s.replyRouter[tab])}`} cfg={s.replyRouter[tab]} state={s} />
        </>
      )}
    </Card>
  );
}

// ─── d. Estimation des coûts ─────────────────────────────────────────────────

export function AiCostEstimateSection() {
  const { t } = useT();
  const q = useAiProvidersState();
  const s = q.data;
  if (!s) return <Card title={t('Estimation des coûts')}>{q.error ? <LoadError /> : <Loading />}</Card>;
  const claude = s.models.anthropic;
  const rows = [...s.tasks].filter((r) => r.calls30d > 0).sort((a, b) => b.cost30dUsd - a.cost30dUsd);
  const idle = s.tasks.length - rows.length;
  const a = s.agentEstimate;
  const budget = s.providers.anthropic.monthlyBudgetUsd;
  const totalBar = a.totalWithBufferUsd == null ? null : budgetBar(a.totalWithBufferUsd, budget);
  // The column count follows the Claude models: a CSS variable, since Tailwind only sees static class names.
  const cols = 'md:grid md:grid-cols-[var(--est-cols)] md:gap-2 md:items-center';
  const colsVar = { '--est-cols': `minmax(0,1.4fr) 76px repeat(${1 + claude.length},minmax(0,1fr))` } as React.CSSProperties;
  const totalCurrent = rows.reduce((x, r) => x + r.cost30dUsd, 0);

  return (
    <Card
      title={t('Estimation des coûts')}
      description={t('Ce que chaque tâche coûterait par mois sur chaque modèle Claude, au trafic des 30 derniers jours.')}
    >
      <div data-estimate style={colsVar}>
        <div className={`hidden ${cols} pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-adm-faint`}>
          <span>{t('Tâche')}</span><span>{t('Appels / mois')}</span><span>{t('Actuel / mois')}</span>
          {claude.map((m) => <span key={m.id} className="truncate" title={m.label}>{m.label.replace(/^Claude /, '')}</span>)}
        </div>
        {rows.map((r) => (
          <div key={r.feature} data-estimate-row={r.feature} className={`py-2 border-t border-adm-line ${cols}`}>
            <p className="text-[12.5px] font-semibold text-adm-ink min-w-0 truncate">
              {t(aiFeatureLabel(r.feature))}
              {r.cacheShare > 0 && <span className="ml-1 text-adm-gold-ink" title={t('hypothèse de cache')}>*</span>}
            </p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1 md:mt-0 md:contents text-[12.5px] tabular-nums">
              <Cell label={t('Appels / mois')}><span className="text-adm-ink-2">{r.calls30d.toLocaleString('fr-FR')}</span></Cell>
              <Cell label={t('Actuel / mois')}><span className="text-adm-ink">{fmtUsd(r.cost30dUsd, 2)}</span></Cell>
              {claude.map((m) => {
                const v = r.projectedUsd[m.id];
                const cheaper = v != null && v < r.cost30dUsd;
                return (
                  <Cell key={m.id} label={m.label}>
                    <span className={v == null ? 'text-adm-faint' : cheaper ? 'text-status-done' : 'text-adm-ink'}>{v == null ? '—' : fmtUsd(v, 2)}</span>
                  </Cell>
                );
              })}
            </div>
          </div>
        ))}
        <div className={`py-2 border-t-2 border-adm-line-strong ${cols} text-[12.5px] font-semibold`}>
          <span className="text-adm-ink">{t('Total')}</span>
          <span className="hidden md:block" />
          <span className="text-adm-ink tabular-nums"> {fmtUsd(totalCurrent, 2)} <small className="font-normal text-adm-muted">· {fmtMad(totalCurrent, s.fxUsd)}</small></span>
        </div>
        {idle > 0 && <p className="text-[11px] text-adm-faint mt-1">{t('{n} tâches sans appel sur 30 jours (non affichées).', { n: idle })}</p>}
        {rows.some((r) => r.cacheShare > 0) && (
          <p className="text-[11px] text-adm-muted mt-1" data-cache-assumption>
            * {t('Réponses WhatsApp : hypothèse de {p} % de l’entrée lue depuis le cache (prompt système + Brain). C’est une hypothèse, pas une mesure.', { p: Math.round((rows.find((r) => r.cacheShare > 0)?.cacheShare ?? 0) * 100) })}
          </p>
        )}
      </div>

      <Sub title={t('Réponses WhatsApp sur Claude')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-agent-estimate>
          <Stat label={t('Aujourd’hui (actuel)')} value={fmtUsd(a.currentUsd, 2)} sub={fmtMad(a.currentUsd, s.fxUsd)} />
          <Stat label={t('Tout Sonnet')} value={fmtUsd(a.allSonnetUsd, 2)} sub={fmtMad(a.allSonnetUsd, s.fxUsd)} />
          <Stat label={t('Mixte Haiku / Sonnet')} value={fmtUsd(a.mixedUsd, 2)}
            sub={a.easyShare == null ? t('part facile inconnue') : t('{p} % de messages faciles', { p: Math.round(a.easyShare * 100) })} />
        </div>
        <p className="text-[11px] text-adm-muted mt-2">
          {t('{n} réponses sur 30 jours', { n: a.calls30d.toLocaleString('fr-FR') })}
          {a.avgInputTokens != null && ` · ${t('{i} tokens en entrée, {o} en sortie en moyenne', { i: Math.round(a.avgInputTokens).toLocaleString('fr-FR'), o: Math.round(a.avgOutputTokens ?? 0).toLocaleString('fr-FR') })}`}
          {` · ${t('cache supposé : {p} %', { p: Math.round(a.cacheShare * 100) })}`}
        </p>
      </Sub>

      <Sub title={t('Total sur Claude vs budget')}>
        <div className="space-y-1.5" data-total-vs-budget>
          <p className="text-xs text-adm-muted">
            {t('Projection')} : <b className="text-adm-ink">{fmtUsd(a.totalProjectedUsd, 2)}</b>
            {' · '}{t('avec 30 % de marge')} : <b className={totalBar ? TONE_TEXT[totalBar.tone] : 'text-adm-ink'}>{fmtUsd(a.totalWithBufferUsd, 2)}</b>
            {' · '}{budget == null ? t('pas de budget Claude') : t('budget Claude {b}', { b: fmtUsd(budget, 0) })}
            {totalBar && ` (${Math.round(totalBar.pct)} %)`}
          </p>
          {totalBar && <Bar pct={totalBar.pct} tone={totalBar.tone} />}
        </div>
      </Sub>
    </Card>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-adm-line bg-adm-surface-2 px-3 py-2 min-w-0">
      <div className="text-[11.5px] text-adm-muted font-medium">{label}</div>
      <div className="text-[18px] font-semibold text-adm-ink tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-adm-muted">{sub}</div>}
    </div>
  );
}
